import type { StoreId } from "@larder/shared";

/**
 * A shop's own app icon.
 *
 * The question this screen asks is "which app do I open?", so the answer should
 * look like the thing being opened — the same tile that is already on the
 * user's home screen. Nothing we could draw competes with that for
 * recognisability, and at this size a colour is read long before a word is.
 *
 * **Committed files, not hot-linked ones.** They are served from our own
 * origin, so the screen owes nothing to five other companies' CDNs while
 * someone is standing in a shop with one bar of signal — and none of them is
 * told which fridge app is showing their name. 96px square, ~5KB each, which is
 * 3× the rendered size and therefore crisp on any phone worth the name.
 *
 * Provenance, so these can be refreshed: each is the Play Store listing icon
 * for the package that claims that shop's links in `stores.ts` — the same
 * package name that was checked against the shop's own `assetlinks.json`. See
 * ADR 0023. Refresh by pulling `og:image` from
 * `play.google.com/store/apps/details?id=<package>` and resizing to 96px.
 */
export function StoreMark({ id }: { id: StoreId }) {
  return (
    <img
      className="store-mark"
      src={`/stores/${id}.png`}
      /* Decorative: the shop's name is the next thing in the button, so a
         screen reader announcing the logo would say it twice. */
      alt=""
      aria-hidden="true"
      width={28}
      height={28}
      /* These are five of the smallest images in the app and all five are
         above the fold on this screen — lazily loading them would trade
         nothing for a flash of empty tiles. */
      loading="eager"
      decoding="async"
    />
  );
}
